/**
 * The typed API client (`03-client-app.md` section 1.2).
 *
 * The web sends the session cookie, the apps send a bearer token. A 401 clears the session and
 * routes to sign in, which is the one place that decision is made.
 */

import { apiErrorSchema, CLIENT_PLATFORM_HEADER, type ErrorCode } from '@goodbuild/shared';
import { isNative, platform } from '../platform/index.js';

export const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

export class ApiRequestError extends Error {
  constructor(
    readonly code: ErrorCode | 'NETWORK' | 'UNKNOWN',
    message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  /** True when signing in again would fix it. */
  get needsSignIn(): boolean {
    return this.code === 'UNAUTHENTICATED';
  }

  get needsAccess(): boolean {
    return this.code === 'ACCESS_REQUIRED';
  }
}

let bearerToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setBearerToken(token: string | null): void {
  bearerToken = token;
}

export function getBearerToken(): string | null {
  return bearerToken;
}

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`${API_URL.replace(/\/$/, '')}/v1${path}`);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = {
    // Tags every request with the platform it came from. The name comes from shared because the
    // API has to allow it in CORS, and a browser preflight fails hard if the two disagree.
    [CLIENT_PLATFORM_HEADER]: platform,
  };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (isNative && bearerToken) headers.Authorization = `Bearer ${bearerToken}`;

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method: options.method ?? (options.body === undefined ? 'GET' : 'POST'),
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      // The web session cookie is set on `.goodbuild.cc`, so it is shared between
      // goodbuild.cc and api.goodbuild.cc and has to ride along.
      credentials: isNative ? 'omit' : 'include',
      signal: options.signal,
    });
  } catch (err) {
    if (options.signal?.aborted) throw err;
    throw new ApiRequestError(
      'NETWORK',
      'We could not reach GOOD BUILD. Check your connection.',
      0,
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const asError = apiErrorSchema.safeParse(parsed);
    if (asError.success) {
      const { code, message, details } = asError.data.error;
      if (code === 'UNAUTHENTICATED') onUnauthenticated?.();
      throw new ApiRequestError(code, message, response.status, details);
    }
    throw new ApiRequestError('UNKNOWN', 'Something went wrong.', response.status);
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),
};

/** Where the browser goes to start Discord sign in on the web. */
export function webSignInUrl(returnTo = '/'): string {
  const url = new URL(`${API_URL.replace(/\/$/, '')}/v1/auth/login`);
  url.searchParams.set('platform', 'web');
  url.searchParams.set('return_to', returnTo);
  return url.toString();
}

/**
 * The login URL an app opens in the system browser.
 *
 * The nonce and the loopback port are added by the native side, which is the one that has to
 * keep the nonce until it exchanges the ticket. Setting them here as well would send each
 * parameter twice and the API would reject the request.
 */
export function appSignInUrl(options: { platform: 'windows' | 'linux' | 'android' }): string {
  const url = new URL(`${API_URL.replace(/\/$/, '')}/v1/auth/login`);
  url.searchParams.set('platform', options.platform);
  return url.toString();
}
